import React, { useEffect, useState, useRef } from "react";
import { Card, Button, Row, Col, Badge } from "react-bootstrap";
import Plot from "react-plotly.js";
import "./HeatmapThreeD.scss";
import useTranslation from "../../../hooks/useTranslation";

const HeatmapThreeD = ({ sensorData = [], deviceCoordinates = [] }) => {
  const { t } = useTranslation("visualization");
  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({});
  const plotRef = useRef(null);
  const [activeView, setActiveView] = useState("isometric");
  const [stats, setStats] = useState({
    minTemp: 0,
    maxTemp: 0,
    avgTemp: 0,
    deviceCount: 0,
    sensorCount: 0,
    timestamp: null,
  });

  useEffect(() => {
    if (!sensorData.length || !deviceCoordinates.length) {
      setPlotData([]);
      return;
    }

    // Calculate statistics from sensor data
    let minTemp = Infinity;
    let maxTemp = -Infinity;
    let tempSum = 0;
    let latestTimestamp = null;

    sensorData.forEach((sensor) => {
      if (sensor.sens_value < minTemp) minTemp = sensor.sens_value;
      if (sensor.sens_value > maxTemp) maxTemp = sensor.sens_value;
      tempSum += sensor.sens_value;

      const sensorTime = new Date(sensor.sens_timestamp);
      if (!latestTimestamp || sensorTime > latestTimestamp) {
        latestTimestamp = sensorTime;
      }
    });

    // Update statistics
    setStats({
      minTemp: minTemp !== Infinity ? minTemp : 0,
      maxTemp: maxTemp !== -Infinity ? maxTemp : 0,
      avgTemp: sensorData.length ? (tempSum / sensorData.length).toFixed(1) : 0,
      deviceCount: Object.keys(
        sensorData.reduce((acc, s) => {
          acc[s.probeid] = true;
          return acc;
        }, {})
      ).length,
      sensorCount: sensorData.length,
      timestamp: latestTimestamp,
    });

    // Improved color gradient function with smoother transitions
    const generateColor = (value) => {
      const normalizedValue = Math.min(
        1,
        Math.max(0, (value - minTemp) / (maxTemp - minTemp))
      );

      let r, g, b;

      if (normalizedValue < 0.2) {
        // Blue to Cyan (0.0 - 0.2)
        r = 0;
        g = Math.round(normalizedValue * 5 * 255);
        b = 255;
      } else if (normalizedValue < 0.4) {
        // Cyan to Green (0.2 - 0.4)
        r = 0;
        g = 255;
        b = Math.round(255 - (normalizedValue - 0.2) * 5 * 255);
      } else if (normalizedValue < 0.6) {
        // Green to Yellow (0.4 - 0.6)
        r = Math.round((normalizedValue - 0.4) * 5 * 255);
        g = 255;
        b = 0;
      } else if (normalizedValue < 0.8) {
        // Yellow to Orange (0.6 - 0.8)
        r = 255;
        g = Math.round(255 - (normalizedValue - 0.6) * 5 * 127);
        b = 0;
      } else {
        // Orange to Red (0.8 - 1.0)
        r = 255;
        g = Math.round(128 - (normalizedValue - 0.8) * 5 * 128);
        b = 0;
      }

      return `rgb(${r}, ${g}, ${b})`;
    };

    // Group sensors by device (probeid)
    const deviceSensors = {};
    sensorData.forEach((sensor) => {
      if (!deviceSensors[sensor.probeid]) {
        deviceSensors[sensor.probeid] = [];
      }
      deviceSensors[sensor.probeid].push(sensor);
    });

    // Calculate container boundaries for better visualization
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    deviceCoordinates.forEach((device) => {
      const x = device.coord_x || 0;
      const y = device.coord_y || 0;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });

    // Add padding to boundaries
    const padding = 10;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = maxX + padding;
    maxY = maxY + padding;

    const traces = [];

    // Add grid lines for better spatial reference
    const gridStep = 10;
    for (
      let x = Math.ceil(minX / gridStep) * gridStep;
      x <= maxX;
      x += gridStep
    ) {
      traces.push({
        type: "scatter3d",
        mode: "lines",
        x: [x, x],
        y: [minY, maxY],
        z: [0, 0],
        line: { color: "#dddddd", width: 1 },
        opacity: 0.3,
        hoverinfo: "none",
        showlegend: false,
      });
    }

    for (
      let y = Math.ceil(minY / gridStep) * gridStep;
      y <= maxY;
      y += gridStep
    ) {
      traces.push({
        type: "scatter3d",
        mode: "lines",
        x: [minX, maxX],
        y: [y, y],
        z: [0, 0],
        line: { color: "#dddddd", width: 1 },
        opacity: 0.3,
        hoverinfo: "none",
        showlegend: false,
      });
    }

    // Add container floor
    traces.push({
      type: "mesh3d",
      x: [minX, maxX, maxX, minX],
      y: [minY, minY, maxY, maxY],
      z: [0, 0, 0, 0],
      i: [0, 0, 0],
      j: [1, 2, 3],
      k: [2, 3, 1],
      opacity: 0.2,
      color: "#e8e8e8",
      hoverinfo: "none",
      showlegend: false,
    });

    // Add container walls (subtle reference)
    traces.push({
      type: "scatter3d",
      mode: "lines",
      x: [minX, maxX, maxX, minX, minX],
      y: [minY, minY, maxY, maxY, minY],
      z: [], // Height of the walls are not defined
      line: { color: "#aaaaaa", width: 2, dash: "dot" },
      opacity: 0.4,
      hoverinfo: "none",
      showlegend: false,
    });

    // Create a trace for each device's sensors
    Object.entries(deviceSensors).forEach(([probeidStr, sensors]) => {
      const probeidNum = Number(probeidStr);
      const device = deviceCoordinates.find((d) => d.probeid === probeidNum);

      if (!device) {
        console.warn(`No matching device found for probeid: ${probeidNum}`);
        return;
      }

      // Sort sensors by sens_num so 1 is at the top
      sensors.sort((a, b) => a.sens_num - b.sens_num);
      const maxSensorNum = Math.max(...sensors.map((s) => s.sens_num));

      // Device coordinates
      const deviceX = device.coord_x || 0;
      const deviceY = device.coord_y || 0;

      // Get device type
      const deviceType = device.probe_type || "standard";

      // Add device identifier
      traces.push({
        type: "scatter3d",
        mode: "text",
        x: [deviceX],
        y: [deviceY],
        z: [maxSensorNum + 1.5],
        text: [`<b>${probeidNum}</b>`],
        textfont: { color: "#333", size: 14, family: "Arial Black" },
        textposition: "top center",
        hoverinfo: "none",
        showlegend: false,
      });

      // Create colored segments for each section between sensors
      for (let i = 0; i < sensors.length - 1; i++) {
        const s1 = sensors[i];
        const s2 = sensors[i + 1];

        const z1 = maxSensorNum - (s1.sens_num - 1);
        const z2 = maxSensorNum - (s2.sens_num - 1);

        // Use average temperature for segment color
        const avgTemp = (s1.sens_value + s2.sens_value) / 2;
        const lineColor = generateColor(avgTemp);

        // Add thick colored line segment
        traces.push({
          type: "scatter3d",
          mode: "lines",
          x: [deviceX, deviceX],
          y: [deviceY, deviceY],
          z: [z1, z2],
          line: {
            color: lineColor,
            width: 15, // Very thick line for visual impact
          },
          opacity: 0.9,
          hoverinfo: "text",
          hovertext: `<b>${t("device")}: ${probeidNum}</b><br>${t("sensors")} ${
            s1.sens_num
          }-${s2.sens_num}<br>${t("avg_temperature")}: ${avgTemp.toFixed(1)}°C`,
          showlegend: false,
        });
      }

      // Add first and last segments (from ground and to top)
      if (sensors.length > 0) {
        // Ground to first sensor
        const firstSensor = sensors[0];
        const firstZ = maxSensorNum - (firstSensor.sens_num - 1);

        traces.push({
          type: "scatter3d",
          mode: "lines",
          x: [deviceX, deviceX],
          y: [deviceY, deviceY],
          z: [0, firstZ],
          line: { color: "#aaaaaa", width: 6 },
          opacity: 0.5,
          hoverinfo: "none",
          showlegend: false,
        });

        // Small markers at each sensor position
        sensors.forEach((sensor) => {
          const sensorZ = maxSensorNum - (sensor.sens_num - 1);

          // Format timestamp for better readability
          const sensorDate = new Date(sensor.sens_timestamp);
          const formattedDate = sensorDate.toLocaleDateString("hu");
          const formattedTime = sensorDate.toLocaleTimeString("hu");

          // Hover text
          const hoverText =
            `<b>${t("sensor")} #${sensor.sens_num}</b><br>` +
            `<b>${t("temperature")}:</b> <span style="color:${generateColor(
              sensor.sens_value
            )}; font-weight:bold">${sensor.sens_value.toFixed(
              1
            )}°C</span><br>` +
            `<b>${t("device")}:</b> ${sensor.probeid} (${deviceType})<br>` +
            `<b>${t("time")}:</b> ${formattedDate} ${formattedTime}`;

          // Add small marker at exact sensor position
          traces.push({
            type: "scatter3d",
            mode: "markers",
            x: [deviceX],
            y: [deviceY],
            z: [sensorZ],
            marker: {
              size: 6,
              color: generateColor(sensor.sens_value),
              opacity: 1,
              symbol: "circle",
            },
            hoverinfo: "text",
            hovertext: hoverText,
            showlegend: false,
          });

          // Add temperature labels next to each sensor
          traces.push({
            type: "scatter3d",
            mode: "text",
            x: [deviceX + 2], // Offset to the right
            y: [deviceY],
            z: [sensorZ],
            text: [`${sensor.sens_value.toFixed(1)}°C`],
            textfont: { color: "#333", size: 10, family: "Arial" },
            textposition: "middle right",
            hoverinfo: "none",
            showlegend: false,
          });
        });

        // Add to legend (only once per device)
        traces.push({
          type: "scatter3d",
          mode: "lines",
          x: [deviceX, deviceX],
          y: [deviceY, deviceY],
          z: [0, 0], // Minimal invisible line for legend
          name: `${t("device")} ${probeidNum}`,
          showlegend: false,
        });
      }
    });

    // Add a color scale reference
    const colorbarTrace = {
      z: [[0]],
      type: "heatmap",
      showscale: true,
      visible: true,
      hoverinfo: "none",
      /* colorscale: [
        [0, 'rgb(0,0,255)'],      // Blue (coldest)
        [0.2, 'rgb(0,255,255)'],  // Cyan
        [0.4, 'rgb(0,255,0)'],    // Green
        [0.6, 'rgb(255,255,0)'],  // Yellow
        [0.8, 'rgb(255,128,0)'],  // Orange
        [1.0, 'rgb(255,0,0)']     // Red (hottest)
      ], */
      colorbar: {
        title: {
          text: `${t("temperature")} (°C)`,
          side: "right",
          font: { size: 14, color: "#333" },
        },
        tickvals: [0, 0.2, 0.4, 0.6, 0.8, 1],
        ticktext: [
          `${minTemp.toFixed(1)}°C`,
          `${(minTemp + (maxTemp - minTemp) * 0.2).toFixed(1)}°C`,
          `${(minTemp + (maxTemp - minTemp) * 0.4).toFixed(1)}°C`,
          `${(minTemp + (maxTemp - minTemp) * 0.6).toFixed(1)}°C`,
          `${(minTemp + (maxTemp - minTemp) * 0.8).toFixed(1)}°C`,
          `${maxTemp.toFixed(1)}°C`,
        ],
        len: 0.8,
        y: 0.5,
        yanchor: "middle",
        outlinewidth: 1,
        outlinecolor: "#888",
        thickness: 15,
      },
    };

    traces.push(colorbarTrace);
    setPlotData(traces);

    // Enhanced layout with better styling
    setLayout({
      title: {
        text: `${t("3d_temp_visualization")}`,
        font: { size: 20, color: "#333", family: "Arial" },
      },
      autosize: true,
      height: 700,
      scene: {
        aspectmode: "manual",
        aspectratio: { x: 1.5, y: 1.5, z: 1 },
        xaxis: {
          title: { text: `${t("probe_row")}`, font: { size: 12 } },
          showgrid: true,
          gridcolor: "#eeeeee",
          zeroline: true,
          zerolinecolor: "#cccccc",
        },
        yaxis: {
          title: { text: `${t("probe_row")}`, font: { size: 12 } },
          showgrid: true,
          gridcolor: "#eeeeee",
          zeroline: true,
          zerolinecolor: "#cccccc",
        },
        zaxis: {
          title: { text: `${t("height_meters")}`, font: { size: 12 } },
          showgrid: true,
          gridcolor: "#eeeeee",
          zeroline: true,
          zerolinecolor: "#cccccc",
        },
        camera: {
          eye: { x: 1.5, y: 1.5, z: 1.5 },
          up: { x: 0, y: 0, z: 1 },
          center: { x: 0, y: 0, z: 0 },
        },
      },
      margin: { l: 0, r: 0, b: 0, t: 50 },
      legend: {
        x: 0,
        y: 1,
        orientation: "h",
        bgcolor: "rgba(255,255,255,0.7)",
        bordercolor: "#ddd",
        borderwidth: 1,
      },
      paper_bgcolor: "rgba(250,250,250,0.9)",
      plot_bgcolor: "rgba(250,250,250,0)",
      hoverlabel: {
        bgcolor: "#fff",
        bordercolor: "#888",
        borderRadius: 9,
        padding: 10,
        namelength: -1,
        font: { family: "Arial", size: 12 },
      },
    });
  }, [sensorData, deviceCoordinates, t]);

  // Handle camera view changes
  const changeView = (view) => {
    setActiveView(view);
    if (!plotRef.current) return;

    const graphDiv = plotRef.current.el;
    if (!graphDiv) return;

    let cameraPosition;
    switch (view) {
      case "top":
        cameraPosition = {
          eye: { x: 0, y: 0, z: 2.5 },
          up: { x: 0, y: 1, z: 0 },
          center: { x: 0, y: 0, z: 0 },
        };
        break;
      case "side":
        cameraPosition = {
          eye: { x: 2.5, y: 0, z: 0.5 },
          up: { x: 0, y: 0, z: 1 },
          center: { x: 0, y: 0, z: 0 },
        };
        break;
      case "front":
        cameraPosition = {
          eye: { x: 0, y: 2.5, z: 0.5 },
          up: { x: 0, y: 0, z: 1 },
          center: { x: 0, y: 0, z: 0 },
        };
        break;
      default: // isometric
        cameraPosition = {
          eye: { x: 1.5, y: 1.5, z: 1.5 },
          up: { x: 0, y: 0, z: 1 },
          center: { x: 0, y: 0, z: 0 },
        };
    }

    window.Plotly?.relayout(graphDiv, { "scene.camera": cameraPosition });
  };

  // Plotly configuration with additional features
  const config = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    toImageButtonOptions: {
      format: "png",
      filename: "szenzor_vizualizacio",
      height: 700,
      width: 1000,
      scale: 2, // Higher resolution
    },
  };

  return (
    <Card className="heatmap-3d-card shadow">
      <Card.Header className="d-flex justify-content-between align-items-center py-3">
        <h5 className="mb-0">{t("3d_temp_visualization")}</h5>
        {stats.timestamp && (
          <small className="text-muted">
            {t("data_time")}: {stats.timestamp.toLocaleString("hu")}
          </small>
        )}
      </Card.Header>

      <Card.Body>
        {plotData.length > 0 ? (
          <>
            <div className="temperature-stats mb-3">
              <Row>
                <Col xs={12} md={6} lg={3} className="mb-2">
                  <div className="stat-card p-2 rounded border">
                    <div className="stat-title">{t("min_temperature")}</div>
                    <div className="stat-value text-primary">
                      {stats.minTemp.toFixed(1)}°C
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3} className="mb-2">
                  <div className="stat-card p-2 rounded border">
                    <div className="stat-title">{t("max_temperature")}</div>
                    <div className="stat-value text-danger">
                      {stats.maxTemp.toFixed(1)}°C
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3} className="mb-2">
                  <div className="stat-card p-2 rounded border">
                    <div className="stat-title">{t("avg_temperature")}</div>
                    <div className="stat-value text-warning">
                      {stats.avgTemp}°C
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3} className="mb-2">
                  <div className="stat-card p-2 rounded border">
                    <div className="stat-title">{t("sensors")}/{t("devices")}</div>
                    <div className="stat-value text-info">
                      {stats.sensorCount} <small>/ {stats.deviceCount}</small>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="camera-controls mb-3">
              <div className="d-flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={
                    activeView === "isometric" ? "primary" : "outline-secondary"
                  }
                  onClick={() => changeView("isometric")}
                >
                 {t("isometric_view")}
                </Button>
                <Button
                  size="sm"
                  variant={
                    activeView === "top" ? "primary" : "outline-secondary"
                  }
                  onClick={() => changeView("top")}
                >
                  {t("top_view")}
                </Button>
                <Button
                  size="sm"
                  variant={
                    activeView === "side" ? "primary" : "outline-secondary"
                  }
                  onClick={() => changeView("side")}
                >
                  {t("side_view")}
                </Button>
                <Button
                  size="sm"
                  variant={
                    activeView === "front" ? "primary" : "outline-secondary"
                  }
                  onClick={() => changeView("front")}
                >
                  {t("front_view")}
                </Button>
              </div>
            </div>

{/*             <div className="legend-info mb-3">
              <small className="text-muted">
                <Badge bg="light" text="dark" className="me-2">
                  ● Kör
                </Badge>
                Flexibilis szonda
                <Badge bg="light" text="dark" className="mx-2">
                  ■ Négyzet
                </Badge>
                Merev szonda
                <Badge bg="light" text="dark" className="mx-2">
                  ◆ Gyémánt
                </Badge>
                Egyéb szenzor
              </small>
            </div> */}

            <Plot
              ref={plotRef}
              data={plotData}
              layout={layout}
              config={config}
              className="heatmap-3d-plot"
              style={{ width: "100%", height: "700px" }}
            />

            <div className="mt-3 text-center">
              <small className="text-muted">
                <i className="fas fa-mouse-pointer me-1"></i>
                {t("rotate_zoom_hint")}
              </small>
            </div>
          </>
        ) : (
          <div className="no-data-message text-center py-5">
            <div className="no-data-icon mb-3">
              <i className="fas fa-cube fa-3x text-muted"></i>
            </div>
            <h3>{t("no_sensor_data")}</h3>
            <p className="text-muted">
            {t("select_other_date")}
            </p>
            <div className="mt-4">
              <Button variant="outline-primary">
              {t("get_latest_data")}
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default HeatmapThreeD;
